import dayjs, { Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// Enable relative time plugin
dayjs.extend(relativeTime);

/**
 * Date formatting utility - Single source of truth for date rendering
 *
 * This module provides consistent date formatting across the application.
 * All date display logic should use these functions instead of calling
 * toLocaleString(), toLocaleDateString(), or dayjs.format() directly.
 */

export type DateInput = string | Date | Dayjs | number | null | undefined;

/**
 * Standard date format: "Jan 15, 2025"
 * Returns 'N/A' for invalid dates or dates before 1900 (epoch/zero dates like 12/31/1899)
 */
export const formatDate = (date: DateInput): string => {
    if (!date) return 'N/A';
    const parsed = dayjs(date);
    if (!parsed.isValid() || parsed.year() < 1900) return 'N/A';
    return parsed.format('MMM D, YYYY');
};

/**
 * Standard time format: "14:30"
 */
export const formatTime = (date: DateInput): string => {
    if (!date) return 'N/A';
    return dayjs(date).format('HH:mm');
};

/**
 * Standard datetime format: "Jan 15, 2025 14:30"
 */
export const formatDateTime = (date: DateInput): string => {
    if (!date) return 'N/A';
    return dayjs(date).format('MMM D, YYYY HH:mm');
};

/**
 * Full datetime with 12-hour format: "Jan 15, 2025, 02:30 PM"
 */
export const formatDateTimeFull = (date: DateInput): string => {
    if (!date) return 'N/A';
    return dayjs(date).format('MMM D, YYYY, hh:mm A');
};

/**
 * Compact date format: "01/15/2025"
 */
export const formatDateCompact = (date: DateInput): string => {
    if (!date) return 'N/A';
    return dayjs(date).format('MM/DD/YYYY');
};

/**
 * ISO date format for API requests: "2025-01-15"
 */
export const formatDateISO = (date: DateInput): string => {
    if (!date) return '';
    return dayjs(date).format('YYYY-MM-DD');
};

/**
 * Chart/timeline format: "Jan '25"
 */
export const formatDateChart = (date: DateInput): string => {
    if (!date) return 'N/A';
    return dayjs(date).format("MMM 'YY");
};

/**
 * Relative time format: "2 hours ago", "3 days ago"
 */
export const formatRelativeTime = (date: DateInput): string => {
    if (!date) return 'N/A';
    return dayjs(date).fromNow();
};

/**
 * Check if a date string is valid
 */
export const isValidDate = (date: DateInput): boolean => {
    if (!date) return false;
    return dayjs(date).isValid();
};

/**
 * Parse date from various formats
 */
export const parseDate = (date: DateInput): Dayjs | null => {
    if (!date) return null;
    const parsed = dayjs(date);
    return parsed.isValid() ? parsed : null;
};

/**
 * Checks if a value is a valid ISO date string
 * Matches ISO 8601 date format with timezone (e.g., 2019-08-12T16:23:41.000Z)
 */
export function isISODateString(value: unknown): boolean {
    if (typeof value !== 'string') return false;

    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;

    if (!isoDateRegex.test(value)) return false;

    return dayjs(value).isValid();
}

/**
 * Formats an ISO date string to a clean human-readable format
 * @param dateString - ISO date string (e.g., "2019-08-12T16:23:41.000Z")
 * @param includeTime - Whether to include time in the output (default: true)
 * @returns Formatted date string (e.g., "2019-08-12 16:23:41" or "2019-08-12")
 */
export function formatISODate(dateString: string, includeTime = true): string {
    const date = dayjs(dateString);

    if (!date.isValid()) {
        return dateString; // Return original if invalid
    }

    if (!includeTime) {
        return date.format('YYYY-MM-DD');
    }

    return date.format('YYYY-MM-DD HH:mm:ss');
}

/**
 * Detects if a column name suggests it contains dates
 */
export function isDateColumn(columnName: string): boolean {
    const lowerName = columnName.toLowerCase();
    const dateKeywords = ['date', 'time', 'datetime', 'timestamp', 'created', 'updated', 'modified'];
    return dateKeywords.some(keyword => lowerName.includes(keyword));
}

/**
 * Formats a value if it's a date, otherwise returns the original value as string
 */
export function formatValueIfDate(value: unknown, includeTime = true): string {
    if (value === null || value === undefined) {
        return '';
    }

    if (isISODateString(value)) {
        return formatISODate(value as string, includeTime);
    }

    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch {
            return Object.prototype.toString.call(value);
        }
    }

    return String(value as string | number | boolean | symbol | bigint);
}

/**
 * Parse ingestion timestamp handling seconds, milliseconds, and microseconds
 * 
 * Handles timestamps in various formats:
 * - < 1e12: seconds (convert to milliseconds)
 * - >= 1e12 and < 1e15: milliseconds (use as-is)
 * - >= 1e15: microseconds (convert to milliseconds)
 * 
 * Also handles ISO date strings and numeric strings.
 */
export function parseIngestionTimestamp(timestamp: string | number | null | undefined): Date | null {
    if (!timestamp) return null;

    let timestampMs: number;

    if (typeof timestamp === 'string') {
        if (timestamp.trim() === '') return null;

        const parsed = parseFloat(timestamp);
        if (isNaN(parsed) || !isFinite(parsed)) {
            // Try parsing as ISO date string
            const date = new Date(timestamp);
            if (isNaN(date.getTime())) return null;
            timestampMs = date.getTime();
        } else {
            timestampMs = parsed;
            // Normalize timestamp to milliseconds
            if (timestampMs >= 1e15) {
                // Timestamp >= 1e15 is likely in microseconds, convert to milliseconds
                timestampMs = timestampMs / 1000;
            } else if (timestampMs < 1e12) {
                // Timestamp < 1e12 is likely in seconds, convert to milliseconds
                timestampMs = timestampMs * 1000;
            }
            // Timestamp between 1e12 and 1e15 is likely already in milliseconds
        }
    } else {
        timestampMs = timestamp;
        // Normalize timestamp to milliseconds
        if (timestampMs >= 1e15) {
            // Timestamp >= 1e15 is likely in microseconds, convert to milliseconds
            timestampMs = timestampMs / 1000;
        } else if (timestampMs < 1e12) {
            // Timestamp < 1e12 is likely in seconds, convert to milliseconds
            timestampMs = timestampMs * 1000;
        }
        // Timestamp between 1e12 and 1e15 is likely already in milliseconds
    }

    const date = new Date(timestampMs);
    if (isNaN(date.getTime())) return null;

    return date;
}

export function formatIngestionDateCompact(timestamp: string | number | null | undefined): string {
    const date = parseIngestionTimestamp(timestamp);
    if (!date) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year} - ${hours}:${minutes}`;
}

export function formatIngestionDate(timestamp: string | number | null | undefined): string {
    const date = parseIngestionTimestamp(timestamp);
    if (!date) return '-';

    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}
